import type { PortalUsageGuideStep } from "@/shared/constants/portalUsageGuide.types";
import { portalUsageGuideStepIndexForPath } from "@/shared/constants/portalUsageGuide.types";
import { COLLABORATION_WALLET_PATH } from "@/shared/constants/collaborationWallet.const";

export type AppUsageGuideStep = PortalUsageGuideStep;

export const APP_USAGE_GUIDE_STEPS: AppUsageGuideStep[] = [
  {
    id: "profile",
    title: "1. Complete and maintain your profile",
    what:
      "My Profile is your company identity page. It includes legal details, capabilities, products/services, documents, and profile visibility.",
    why:
      "A complete and accurate profile builds trust and improves your chance of getting relevant requests from serious partners.",
    howToUse: [
      "Update legal details, capabilities, products, and documents",
      "Use the Anonymized and Matched view toggle to preview visibility",
      "Apply for verification to increase credibility",
    ],
    path: "/profile",
  },
  {
    id: "discover",
    title: "2. Explore companies",
    what:
      "Explore is your discovery engine to browse and evaluate businesses that may align with your collaboration goals.",
    why:
      "It helps you identify high-fit partners faster instead of relying on random outreach, saving time and improving match quality.",
    howToUse: [
      "Open company cards and compare fit before sending interest",
      "Review capability overlap and strategic alignment",
      "Use shortlist to save strong matches for later",
    ],
    path: "/my-discovery",
  },
  {
    id: "shortlist",
    title: "3. Curate your shortlist",
    what:
      "Shortlist is where you keep companies you want to revisit or contact later, so promising fits do not get lost as you browse.",
    why:
      "A focused shortlist speeds up decisions and keeps your outreach organized when you are ready to send interest.",
    howToUse: [
      "Add companies from discovery when you want to compare or follow up",
      "Open a saved company when you are ready to review details or act",
      "Trim the list so it stays a clear set of real opportunities",
    ],
    path: "/shortlist",
  },
  {
    id: "dashboard",
    title: "4. Use your dashboard",
    what:
      "Dashboard is your home for inbound and outbound collaboration requests, with full visibility on pending interactions.",
    why:
      "Quick and clear response handling prevents missed opportunities and keeps your pipeline active and organized.",
    howToUse: [
      "Use Received and Sent tabs to review and manage pending requests",
      "Respond quickly to improve match conversion",
    ],
    path: "/dashboard",
  },
  {
    id: "collaborations",
    title: "5. Work on active collaborations",
    what:
      "Collaborations is the active workspace where matched companies continue discussions, align requirements, and move execution forward.",
    why:
      "This page turns a match into real business progress by keeping communication, context, and decision points in one place.",
    howToUse: [
      "Share context, documents, and next actions",
      "Track progress with your matched company",
      "Use workspace tools to keep communication structured",
    ],
    path: "/collaborations",
  },
  {
    id: "collaborations-wallet",
    title: "6. Manage collaborations and settings",
    what:
      "Your collaboration wallet and Settings control account readiness, platform access continuity, and support-related account preferences.",
    why:
      "Monitoring your collaboration balance and account settings ensures you can continue outreach, respond quickly, and avoid workflow interruption.",
    howToUse: [
      "Check available collaborations and buy more when required",
      "Review account settings and support options",
      "Keep account information updated for uninterrupted access",
    ],
    path: COLLABORATION_WALLET_PATH,
  },
];

/** Main sidebar list destinations only (floating guide bot appears here). */
export const APP_USAGE_GUIDE_SIDEBAR_ROOT_PATHS = [
  "/profile",
  "/my-discovery",
  "/shortlist",
  "/dashboard",
  "/collaborations",
] as const;

export function isAppUsageGuideSidebarRootPath(pathname: string): boolean {
  return (APP_USAGE_GUIDE_SIDEBAR_ROOT_PATHS as readonly string[]).includes(pathname);
}

export function appUsageGuideStepIndexForPath(pathname: string): number {
  return portalUsageGuideStepIndexForPath(APP_USAGE_GUIDE_STEPS, pathname);
}
